import { ErrorEnvelopeSchema, type ErrorCode } from '@saniplus/contracts';

/**
 * The single error shape every caller in both apps switches on. `code` is the
 * frozen `ErrorCode` enum, never a string — a code the UI cannot switch on
 * must never reach a component.
 */
export interface ApiError {
  readonly status: number;
  readonly code: ErrorCode;
  readonly message: string;
  readonly details?: unknown;
  readonly requestId: string;
}

export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'code' in value &&
    'status' in value &&
    'requestId' in value
  );
}

/** Status → the code to fall back to when the body is not a conforming envelope. */
function fallbackCode(status: number): ErrorCode {
  if (status === 0) return 'SERVICE_UNAVAILABLE';
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 503) return 'SERVICE_UNAVAILABLE';
  return 'INTERNAL_ERROR';
}

/**
 * Map an HTTP failure to `ApiError`.
 *
 * A body that does not parse as the frozen error envelope is not trusted for
 * its `code`: an intermediary (proxy, dev server, offline browser) can produce
 * a 502 with HTML, and inventing a code from it would put a value outside the
 * enum into a `switch`.
 */
export function toApiError(status: number, body: unknown, requestId = 'unknown'): ApiError {
  const parsed = ErrorEnvelopeSchema.safeParse(body);
  if (parsed.success) {
    const { code, message, details, requestId: id } = parsed.data.error;
    return details === undefined
      ? { status, code, message, requestId: id }
      : { status, code, message, details, requestId: id };
  }
  return {
    status,
    code: fallbackCode(status),
    message: typeof body === 'string' && body.length > 0 ? body : `HTTP ${String(status)}`,
    requestId,
  };
}

/** A fetch rejection (offline, DNS, aborted socket) as an `ApiError`. */
export function networkError(cause: unknown): ApiError {
  return {
    status: 0,
    code: 'SERVICE_UNAVAILABLE',
    message: cause instanceof Error ? cause.message : 'network error',
    requestId: 'offline',
  };
}
